import {
  Inject,
  Injectable,
  Optional,
  reflectComponentType,
  DOCUMENT
} from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayConfig } from '@angular/cdk/overlay';

import { BehaviorSubject } from 'rxjs';
import { ScreenReaderService } from '../../services/screen-reader.service';
import { HELP_LINK_ARTICLE_RESOLVER, HELP_LINK_COMPONENT_TYPE } from './help-link.domain';

import type { ComponentRef, OnDestroy } from '@angular/core';
import type { ComponentType, OverlayRef } from '@angular/cdk/overlay';
import type { Subscription } from 'rxjs';
import type { HelpArticleResolver, IHelpComponent, HelpLinkData } from './help-link.domain';

class HelpLinkOverlayRefContainer {
  private elementsFocusedBefore: Array<HTMLElement> = [];
  ariaHiddenElements: Map<Element, string> = new Map<Element, string>();

  constructor(private overlayRef: OverlayRef, private document: Document) {
    const overlayContainer = this.document.getElementsByClassName('cdk-overlay-container');
    if (overlayContainer !== undefined && overlayContainer !== null && overlayContainer.length > 0) {
      this.elementsFocusedBefore = ScreenReaderService.contentHidden(overlayContainer[0] as HTMLElement, this.ariaHiddenElements, this.elementsFocusedBefore, this.document);
    }
  }

  destroy(): void {
    ScreenReaderService.contentVisible(this.ariaHiddenElements, this.elementsFocusedBefore, this.document);
    this.overlayRef.dispose();
  }
}

/**
 * Handles launching of the help component
 */
@Injectable({
  providedIn: 'root'
})
export class HelpLinkService implements OnDestroy {
  readonly isHelpOverlayOpen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private overlaySubscription: Subscription;
  private overlayRefContainer: HelpLinkOverlayRefContainer;
  private contentRef: ComponentRef<IHelpComponent>;

  constructor(@Inject(Overlay) private overlay: Overlay,
              @Inject(DOCUMENT) private document: Document,
              @Optional() @Inject(HELP_LINK_COMPONENT_TYPE) private readonly componentType: ComponentType<IHelpComponent>,
              @Inject(HELP_LINK_ARTICLE_RESOLVER) private readonly helpArticleResolver: HelpArticleResolver) {
  }

  ngOnDestroy(): void {
    this.close();
  }

  open(articleId: string): void {
    if (this.overlayRefContainer === undefined && this.componentType !== undefined && this.componentType !== null) {
      const overlayRef = this.createOverlay();
      overlayRef.backdropClick().subscribe((): void => this.close());
      this.overlayRefContainer = new HelpLinkOverlayRefContainer(overlayRef, this.document);
      const componentPortal = new ComponentPortal(this.componentType);
      this.contentRef = overlayRef.attach(componentPortal);
      this.overlaySubscription = this.contentRef.instance.closeEvent.subscribe((): void => this.close());
      this.isHelpOverlayOpen.next(true);
    }
    if (this.contentRef !== undefined && articleId !== undefined) {
      this.setInputBindings<IHelpComponent>(this.contentRef, { articleId });
    }
  }

  close(): void {
    if (this.overlayRefContainer !== undefined) {
      this.overlayRefContainer.destroy();
      this.overlayRefContainer = undefined;
      this.isHelpOverlayOpen.next(false);
    }
    if (this.overlaySubscription !== undefined) {
      this.overlaySubscription.unsubscribe();
      this.overlaySubscription = undefined;
    }
  }

  getHelpArticleId(): string {
    if (this.helpArticleResolver !== undefined && this.helpArticleResolver !== null) {
      return this.helpArticleResolver.getHelpArticleId();
    }
  }

  private createOverlay(): OverlayRef {
    const overlayConfig = this.getOverlayConfig();
    return this.overlay.create(overlayConfig);
  }

  /**
   * @returns The overlay configuration.
   */
  private getOverlayConfig(): OverlayConfig {
    return new OverlayConfig({
      panelClass: 'td-wb-overlay-panel',
      positionStrategy: this.overlay.position().global().top('0').right('0'),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
  }

  /**
   * Sets components inputs from modal data
   *
   * @param contentRef
   * @param modalData
   */
  private setInputBindings<T extends IHelpComponent>(contentRef: ComponentRef<T>, modalData: HelpLinkData): void {
    // there is no way to get component's metadata with AOT compilation. Have to use component factory to get inputs
    const componentInputs = reflectComponentType(contentRef.componentType)?.inputs;

    Object.keys(modalData).forEach((key: string): void => {
      const matchingInput = componentInputs.find(
        (input: { propName: string; templateName: string }): boolean => input.templateName === key);
      if (matchingInput !== undefined) {
        contentRef.instance[matchingInput.propName] = modalData[matchingInput.templateName];
      }
    });
  }
}
